import type { TournamentDataSaver } from './TournamentDataSaver';
import type { PlatformLeaderboards, PlatformUser, LeaderboardEntry } from '../platform/Platform';
import type { TournamentSaveData } from './types';
import fakeUsers from '../leaderboard/fakeUsers.json';
import RandomDataGenerator from '../../libs/RandomDataGenerator';

interface TournamentLeaderboardHandlerDependencies {
  dataSaver: TournamentDataSaver;
  leaderboards: PlatformLeaderboards;
  currentPlayer: PlatformUser;
}

export class TournamentLeaderboardHandler {
  private readonly leaderboardName = 'tournament';

  private readonly rnd = new RandomDataGenerator([Math.random()]);

  constructor(private readonly dependencies: TournamentLeaderboardHandlerDependencies) {}

  public createLeaderboardGetter(tournament: TournamentSaveData): () => Promise<LeaderboardEntry[]> {
    const { leaderboards } = this.dependencies;

    const leaderboardId = `${this.leaderboardName}.${tournament.contextId}`;

    return async (): Promise<LeaderboardEntry[]> => {
      const realEntries = await leaderboards.getEntries(leaderboardId, 4);
      const entries = realEntries ?? this.generateFakeEntries();
      const hasPlayerAlready = entries.some((entry) => entry.player.currentUser);

      if (!hasPlayerAlready) {
        const realPlayerEntry = await leaderboards.getPlayerEntry(leaderboardId);
        entries.push(realPlayerEntry ?? this.getPlayerScoreBasedEntry(tournament));
      }

      entries.sort((entryA, entryB) => entryB.score - entryA.score);
      entries.forEach((entry, index) => {
        entry.rank = index + 1;
      });

      return entries;
    };
  }

  private generateFakeEntries(): LeaderboardEntry[] {
    const entriesAmount = this.rnd.integerInRange(2, 4);

    const fakePlayers: LeaderboardEntry['player'][] = this.rnd
      .shuffle(fakeUsers.slice())
      .slice(0, entriesAmount)
      .map(({ name, avatarUrl }) => ({
        name,
        avatarUrl,
        currentUser: false,
        id: this.rnd.uuid(),
      }));

    const scores = new Array(entriesAmount)
      .fill(null)
      .map(() => Math.floor(this.rnd.integerInRange(100, 1000) / 5) * 5)
      .sort((a, b) => b - a);

    return fakePlayers.map((player, index) => ({
      player,
      score: scores[index],
      rank: index + 1,
    }));
  }

  private getPlayerScoreBasedEntry(tournament: TournamentSaveData): LeaderboardEntry {
    const { currentPlayer } = this.dependencies;

    return {
      player: {
        name: currentPlayer.name,
        avatarUrl: currentPlayer.avatarUrl,
        id: currentPlayer.id,
        currentUser: true,
      },
      score: tournament.userScore,
      rank: 1,
    };
  }

  public async updateLeaderboardScore(tournament: TournamentSaveData): Promise<void> {
    const { leaderboards } = this.dependencies;
    await leaderboards.setScore(`${this.leaderboardName}.${tournament.contextId}`, tournament.userScore);
  }
}
